/**
 * Contact Page Component
 *
 * Customer contact page with form, office information, and map placeholder
 * Following PRP specification lines 762-773
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ContactForm } from '../../components/cpr/ContactForm';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';

export const Contact = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Contact Us | CPR Training Direct Orlando & Atlanta</title>
        <meta name="title" content="Contact Us | CPR Training Direct Orlando & Atlanta" />
        <meta
          name="description"
          content="Contact CPR Training Direct for questions about certification classes. Offices in Orlando, FL and Atlanta, GA. Reach out through our online contact form today."
        />
        <meta name="keywords" content="contact CPR training, Orlando CPR office, Atlanta CPR classes, CPR certification help" />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/contact" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/contact" />
        <meta property="og:title" content="Contact Us | CPR Training Direct Orlando & Atlanta" />
        <meta
          property="og:description"
          content="Contact CPR Training Direct for questions about certification classes. Offices in Orlando, FL and Atlanta, GA. Reach out through our online contact form today."
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/contact" />
        <meta name="twitter:title" content="Contact Us | CPR Training Direct Orlando & Atlanta" />
        <meta
          name="twitter:description"
          content="Contact CPR Training Direct for questions about certification classes. Offices in Orlando, FL and Atlanta, GA. Reach out through our online contact form today."
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />
      </Helmet>

      {/* Hero Section - Brutalist */}
      <div className="bg-indigo_dye-500 text-white py-16 md:py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1
            data-aos="fade-up"
            className="font-display text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-wider mb-6"
          >
            Get in Touch
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-body text-xl md:text-2xl text-indigo_dye-100 leading-relaxed"
          >
            We're here to answer your questions and help you get certified
          </p>
        </div>
      </div>

      <div className="min-h-screen bg-white py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Contact Form Column */}
            <div>
              <ContactForm />
            </div>

            {/* Contact Information Column */}
            <div className="space-y-8">
              <div data-aos="fade-left" data-aos-delay="100">
                <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-wider text-indigo_dye-500 mb-8">
                  Our Offices
                </h2>

                {/* Consolidated Office Information */}
                <Card variant="brutalist">
                  <div className="space-y-6 font-body text-gray-700">
                    {/* Service Areas */}
                    <div>
                      <h3 className="font-mono text-xl font-bold uppercase tracking-wide text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-2xl">📍</span>
                        Orlando, Florida
                      </h3>
                      <p className="ml-8 font-semibold">Serving the greater Orlando area</p>
                    </div>

                    <div>
                      <h3 className="font-mono text-xl font-bold uppercase tracking-wide text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-2xl">📍</span>
                        Atlanta, Georgia
                      </h3>
                      <p className="ml-8 font-semibold">Serving the greater Atlanta area</p>
                    </div>

                    {/* Office Hours */}
                    <div className="pt-4 border-t-4 border-gray-200">
                      <h3 className="font-mono text-lg font-bold uppercase tracking-wide text-gray-900 mb-3">
                        Office Hours
                      </h3>
                      <div className="ml-0">
                        <p className="font-semibold">Mon-Fri: 8:00 AM - 6:00 PM</p>
                        <p className="font-semibold">Sat-Sun: 9:00 AM - 3:00 PM</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>


              {/* Quick Response Info */}
              <div data-aos="fade-left" data-aos-delay="300">
              <Card variant="brutalist" title="RESPONSE TIMES" className="bg-amaranth_purple-50">
                <ul className="space-y-3 font-body text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-amaranth_purple-600 text-2xl">⚡</span>
                    <span><strong className="font-mono uppercase">Contact Form:</strong> Within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amaranth_purple-600 text-2xl">⚡</span>
                    <span><strong className="font-mono uppercase">Booking Requests:</strong> Confirmed within 24 hours</span>
                  </li>
                </ul>
              </Card>
              </div>
            </div>
          </div>

          {/* Quick Booking CTA - Brutalist */}
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="bg-amaranth_purple-500 border-4 border-amaranth_purple-700 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-12 md:p-16 text-center text-white"
          >
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider mb-6">
              Ready to Get Certified?
            </h2>
            <p className="font-body text-xl md:text-2xl text-amaranth_purple-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Don't wait! Book your CPR training class today and get certified by tomorrow.
            </p>
            <Link to="/book">
              <Button
                variant="primary"
                size="xl"
                className="bg-white border-4 border-white !text-gray-900 hover:bg-amaranth_purple-900 hover:!text-white hover:border-amaranth_purple-900 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] hover:shadow-[12px_12px_0px_0px_rgba(2,5,18,1)]"
              >
                Book Your Class Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
